import { memo } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';

import { AccessElements } from 'helpers/app.config';
import { useGlobalContext } from 'context/GlobalContext';

type Props = {
  handleReadAll: () => void;
  handleShowAllNotifications: () => void;
  disabled: boolean;
  realTimeTotalCount: number;
};

const buttonsStyle = {
  backgroundColor: '#1D6FCC',
  color: '#FFF',
  height: 38,
  borderRadius: 1,
  fontSize: '12px',
  marginTop: {
    xs: '10px',
    sm: '0px'
  },
  width: {
    xs: '100%',
    sm: 'auto'
  }
};

/**
 * MarkAllReadButton component renders a button to mark all notifications as read
 * and optionally a button to show all notifications based on user permissions.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.handleReadAll - Function to handle marking all notifications as read.
 * @param {Function} props.handleShowAllNotifications - Function to handle showing all notifications.
 * @param {boolean} props.disabled - Indicates if the button should be disabled.
 * @param {number} props.realTimeTotalCount - The real-time total count of notifications.
 *
 * @returns {JSX.Element} The rendered MarkAllReadButton component.
 */
function MarkAllReadButton({
  handleReadAll,
  handleShowAllNotifications,
  disabled,
  realTimeTotalCount
}: Props): JSX.Element {
  const { hasPerm } = useGlobalContext();
  const { t } = useTranslation('global');

  return (
    <Box
      marginTop={2}
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        '.css-1m8qhi0-MuiButtonBase-root-MuiButton-root': {
          padding: '8px 12px'
        },
        flexDirection: {
          xs: 'column',
          sm: 'row'
        }
      }}
    >
      <Typography
        sx={{
          color: '#ACACAC',
          fontSize: 14,
          fontWeight: 700
        }}
        variant="caption"
      >
        {t('pageTitles.notifications')} ({realTimeTotalCount || 0})
      </Typography>
      <Box>
        <Button
          disabled={disabled}
          onClick={handleReadAll}
          size="small"
          sx={buttonsStyle}
          type="button"
          variant="contained"
        >
          {t('notifications.allRead')}
        </Button>
        {hasPerm([AccessElements.NotificationSearch]) && (
          <Button
            onClick={handleShowAllNotifications}
            size="small"
            sx={{ ...buttonsStyle, ml: 1 }}
            type="button"
            variant="contained"
          >
            {t('notifications.showAll')}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default memo(MarkAllReadButton);
